import React, { useCallback, useEffect, useState } from 'react';
import api from '@/utils/api';
import { Tag, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const fmtAmount = (c) => {
  if (c.discount_type === 'percent') return `${c.amount}% off`;
  if (c.discount_type === 'commission_percent') return `${c.amount}% of commission`;
  return `$${Number(c.amount).toFixed(2)} off`;
};

// Every reselling tenant's own coupon defaults to (and is locked to)
// "tenant_absorbs" from their own Coupons admin page - this screen is the
// ONLY place "shared" can ever be set, since it means ImpactGolf agrees to
// co-fund half of that specific coupon's discount.
export default function AffiliateCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/fulfillment-coupons');
      setCoupons(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load affiliate coupons');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleModeChange = async (coupon, mode) => {
    if (mode === coupon.discount_split_mode) return;
    setSavingId(coupon.coupon_id);
    try {
      await api.put(`/admin/fulfillment-coupons/${coupon.coupon_id}/split-mode`, {
        discount_split_mode: mode,
      });
      setCoupons((prev) =>
        prev.map((c) => (c.coupon_id === coupon.coupon_id ? { ...c, discount_split_mode: mode } : c))
      );
      toast.success(
        mode === 'shared'
          ? `ImpactGolf now co-funds half of "${coupon.code}"`
          : `"${coupon.code}" reverted to tenant-funded only`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update split mode');
    } finally {
      setSavingId(null);
    }
  };

  const filtered = coupons.filter((c) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return c.code.toLowerCase().includes(q) || (c.reseller_name || '').toLowerCase().includes(q);
  });

  if (loading && coupons.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#334155] border-r-transparent" />
        <p className="mt-4 text-gray-600">Loading affiliate coupons...</p>
      </div>
    );
  }

  return (
    <div data-testid="affiliate-coupons">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Affiliate Coupons
        </h2>
        <span className="text-sm text-gray-500" data-testid="affiliate-coupon-count">
          {coupons.length} coupons across your resellers
        </span>
      </div>

      <p className="text-sm text-gray-600 mb-6">
        Review coupons created by your fulfillment resellers. Setting a coupon to{' '}
        <span className="font-semibold">Shared</span> means ImpactGolf co-funds half of that
        coupon's discount out of its own profit share - resellers cannot set this themselves.
      </p>

      <div className="mb-6">
        <div className="relative max-w-md">
          <Input
            placeholder="Search by code or reseller..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-gray-50 border-[#E8E4DC]"
            data-testid="affiliate-coupon-search"
          />
        </div>
      </div>

      <div className="overflow-x-auto border border-[#E8E4DC] rounded-lg">
        <table className="w-full text-sm" data-testid="affiliate-coupon-table">
          <thead className="bg-gray-50 border-b border-[#E8E4DC]">
            <tr>
              <th className="text-left px-4 py-3 font-bold text-gray-700">Code</th>
              <th className="text-left px-4 py-3 font-bold text-gray-700">Reseller</th>
              <th className="text-left px-4 py-3 font-bold text-gray-700">Discount</th>
              <th className="text-left px-4 py-3 font-bold text-gray-700">Status</th>
              <th className="text-left px-4 py-3 font-bold text-gray-700">Cost split</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F3F4F6]">
            {filtered.map((c) => (
              <tr key={c.coupon_id} className="hover:bg-gray-50/50" data-testid={`affiliate-coupon-row-${c.coupon_id}`}>
                <td className="px-4 py-3 font-mono font-bold text-[#2C3E2D]">{c.code}</td>
                <td className="px-4 py-3 text-gray-700">
                  <div className="flex items-center gap-2">
                    <Users className="h-3.5 w-3.5 text-gray-400" />
                    {c.reseller_name}
                  </div>
                </td>
                <td className="px-4 py-3 text-gray-700">{fmtAmount(c)}</td>
                <td className="px-4 py-3">
                  {c.is_active ? (
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-bold rounded-full bg-[#4A6741]/10 text-[#4A6741]">
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-bold rounded-full bg-gray-200 text-gray-600">
                      Paused
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <Select
                    value={c.discount_split_mode || 'tenant_absorbs'}
                    onValueChange={(v) => handleModeChange(c, v)}
                    disabled={savingId === c.coupon_id}
                  >
                    <SelectTrigger className="w-44" data-testid={`affiliate-coupon-split-${c.coupon_id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tenant_absorbs">Tenant absorbs</SelectItem>
                      <SelectItem value="shared">Shared 50/50</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filtered.length === 0 && !loading && (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-[#E8E4DC] mt-4">
          <Tag className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">
            {coupons.length === 0 ? 'No resellers have created any coupons yet.' : 'No coupons match your search.'}
          </p>
        </div>
      )}
    </div>
  );
}
